import { forwardRef, useCallback, useState, useEffect } from "react";
import { InputProps } from "./Input";
import circlePlusSrc from "$/circle-plus.svg";
import trashSrc from "$/trash.svg";
import { AnimatePresence, motion } from "framer-motion";
import { useConfirmTouch } from "../hooks/common/useConfirmTouch";
import { twMerge } from "tailwind-merge";
import { withValidate } from "./withValidate";
import { validateImageFile } from "../utils/fileValidation";

interface UploaderProps extends Omit<InputProps, "onError"> {
  onUploaded?: (image?: string | File) => unknown;
  className?: string;
  defaultValue?: string;
  maxSizeMB?: number;
  onValidationError?: (error: string) => void;
}

export const Uploader = forwardRef<HTMLInputElement, UploaderProps>(
  (
    {
      onUploaded,
      className,
      defaultValue,
      maxSizeMB = 10,
      onValidationError,
      ...props
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const [preview, setPreview] = useState<string | undefined | null>(
      defaultValue || null
    );

    // 메모리 누수 방지를 위한 cleanup
    useEffect(() => {
      return () => {
        // FileReader 방식에서는 Base64 문자열이므로 별도 해제 불필요
        // preview 상태는 React가 자동으로 정리
      };
    }, [preview, defaultValue]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = e;
        if (target?.files?.[0]) {
          const file = target.files[0];

          // 안드로이드에서 파일 정보 로깅 (디버깅용)
          console.log("Selected file:", {
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          });

          // 이미지 파일 검증
          const validation = validateImageFile(file, maxSizeMB);
          if (!validation.isValid) {
            console.error("File validation failed:", validation.error);
            onValidationError?.(
              validation.error || "파일 검증에 실패했습니다."
            );
            target.value = "";
            return;
          }

          try {
            // 웹앱 호환성을 위해 FileReader 사용 (미리보기용)
            const reader = new FileReader();

            reader.onload = (event) => {
              const result = event.target?.result;
              if (typeof result === "string") {
                console.log(
                  "FileReader 성공:",
                  result.substring(0, 50) + "..."
                );
                setPreview(result);
                // API 호환성을 위해 File 객체 전달
                onUploaded?.(file);
              } else {
                console.error("FileReader 결과가 문자열이 아님");
                onValidationError?.("이미지 파일을 읽을 수 없습니다.");
              }
            };

            reader.onerror = (error) => {
              console.error("FileReader 에러:", error);
              onValidationError?.("이미지 파일 읽기에 실패했습니다.");
            };

            reader.onabort = () => {
              console.log("FileReader 중단됨");
              onValidationError?.("이미지 파일 읽기가 중단되었습니다.");
            };

            // Base64로 읽기 (웹앱에서 더 안정적)
            reader.readAsDataURL(file);
          } catch (error) {
            console.error("Error reading file:", error);
            onValidationError?.("이미지 파일 처리에 실패했습니다.");
            target.value = "";
            return;
          }
        }
        target.value = "";
      },
      [onUploaded, maxSizeMB, onValidationError]
    );

    const { bindClick, idle } = useConfirmTouch();

    const handleClear = useCallback(() => {
      setFocus(false);
      setPreview(undefined);
      onUploaded?.(undefined);
    }, [onUploaded]);

    return (
      <>
        <label className={twMerge("flex flex-col gap-2", className)}>
          {!preview && (
            <div
              className={twMerge(
                "outline-none flex items-center h-10 border border-inherit rounded cursor-pointer",
                focus && "outline outline-yellow outline-offset-2 outline-2"
              )}
              tabIndex={0}
              onBlurCapture={setFocus.bind(null, false)}
              onFocusCapture={setFocus.bind(null, true)}
              onTouchStart={() => setFocus(true)}
              onTouchEnd={() => setFocus(false)}
            >
              <img
                src={circlePlusSrc}
                alt="파일 첨부하기 이미지"
                className="m-auto"
              />
              <input
                {...props}
                onChange={handleChange}
                className="hidden"
                type="file"
                accept="image/*"
                ref={ref}
              />
            </div>
          )}
        </label>

        {preview && (
          <div
            className="overflow-hidden relative mt-2 rounded"
            onClick={bindClick(handleClear)}
          >
            <motion.img
              src={preview}
              alt="이미지 미리보기"
              className="origin-top"
              initial={{
                opacity: 0,
                transform: "scale(1.2)",
              }}
              animate={{
                opacity: 1,
                transform: "scale(1)",
              }}
              exit={{
                position: "absolute",
                opacity: 0,
                transform: "scale(1.2)",
              }}
            />
            <AnimatePresence>
              {idle && (
                <motion.i
                  className="flex absolute inset-0 bg-black bg-opacity-30 pointer-events-none"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                >
                  <img src={trashSrc} alt="삭제버튼" className="m-auto" />
                </motion.i>
              )}
            </AnimatePresence>
          </div>
        )}
      </>
    );
  }
);

export const ValidateUploader = withValidate(Uploader);
